'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import {
  Dialog,
  DialogDismiss,
  DialogHeading,
  DialogProvider,
  Form,
  FormGroup,
  FormGroupLabel,
  FormRadioGroup,
  FormReset,
  FormSubmit,
  useFormStore,
} from '@ariakit/react';
import cx from 'classnames';

import { recomputeViewport } from '~/system/recomputeViewport';
import CloseIcon from '~/ui/assets/icons/close.svg';
import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';
import { AsyncButton } from '~/ui/components/button/async';
import { Expandable } from '~/ui/components/expandable';
import { FilterBar } from '~/ui/components/filters/bar';
import { FormCheckbox } from '~/ui/components/form/checkbox';
import { FormRadio } from '~/ui/components/form/radio';
import { FormRange } from '~/ui/components/form/range';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { type Filter } from '~/ui/systems/filter';

import styles from './filters.module.scss';

const RANGE_MAX_SUFFIX = '-max';
const RANGE_MIN_SUFFIX = '-min';

export interface FilterProps {
  query?: string;
  eyebrow?: string;
  title: string;
  filters: Filter[];
  isSticky: boolean;
  filterBarRef?: React.Ref<HTMLDivElement>;
  scrollOnSubmit: boolean;
}

export function Filters({
  query,
  eyebrow,
  title,
  filters,
  isSticky,
  filterBarRef,
  scrollOnSubmit,
}: FilterProps): React.ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const form = useFormStore({
    defaultValues: getDefaultValues(filters),
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedFilterCount, setSelectedFilterCount] = useState(0);

  const applyCurrentFilters = useCallback(() => {
    let selectedCount = 0;

    function applyFilter(filter: Filter) {
      switch (filter.type) {
        case 'checkbox':
          filter.options.forEach(option => {
            form.setValue(option.id, option.selected);

            if (option.selected && filter.includeInPills) {
              selectedCount++;
            }
          });

          break;

        case 'radio':
          for (const option of filter.options) {
            if (option.selected) {
              form.setValue(option.id, option.name);

              if (filter.includeInPills) {
                selectedCount++;
              }

              break;
            }
          }

          break;

        case 'range':
          filter.options.forEach(option => {
            form.setValue(`${option.id}${RANGE_MIN_SUFFIX}`, option.value.min);
            form.setValue(`${option.id}${RANGE_MAX_SUFFIX}`, option.value.max);

            if (option.selected && filter.includeInPills) {
              selectedCount++;
            }
          });

          break;

        case 'combo':
          filter.options.forEach(applyFilter);
          break;
      }
    }

    filters.forEach(applyFilter);
    setSelectedFilterCount(selectedCount);
  }, [filters, form]);

  useEffect(() => {
    applyCurrentFilters();
  }, [applyCurrentFilters]);

  useEffect(() => {
    if (!sidebarOpen) {
      // recomputeViewport in case screen size has changed while modal was open
      // i think this is due to the ariakit Dialog component saving the state of the body style before the dialog is opened, and then restoring it after closing.
      setTimeout(() => {
        recomputeViewport();
      }, 400);
    }
  }, [sidebarOpen]);

  function getDefaultValues(filters: Filter[]): Record<string, string | boolean> {
    return filters.reduce((acc, filter) => {
      switch (filter.type) {
        case 'checkbox':
          return {
            ...acc,
            ...filter.options.reduce(
              (acc, { id }) => ({
                ...acc,
                [id]: false,
              }),
              {},
            ),
          };

        case 'radio':
          return { ...acc, [filter.name]: '' };

        case 'range':
          return {
            ...acc,
            ...filter.options.reduce(
              (acc, { id }) => ({
                ...acc,
                [`${id}${RANGE_MIN_SUFFIX}`]: '',
                [`${id}${RANGE_MAX_SUFFIX}`]: '',
              }),
              {},
            ),
          };

        case 'combo':
          return { ...acc, ...getDefaultValues(filter.options) };
      }
    }, {});
  }

  function renderFilterOptions(filter: Filter): React.ReactNode[] {
    switch (filter.type) {
      case 'checkbox':
        return filter.options.map(({ id, name }, index) => (
          <FormCheckbox key={index} className={styles.option} name={id} label={name} />
        ));

      case 'radio':
        return filter.options.map(({ id, name }, index) => {
          return (
            <FormRadio key={index} className={styles.option} name={id} value={name} label={name} />
          );
        });

      case 'range':
        return filter.options.map(({ id, label, inputLabels, validations }, index) => {
          return (
            <FormRange
              key={index}
              className={styles.option}
              name={{ min: `${id}${RANGE_MIN_SUFFIX}`, max: `${id}${RANGE_MAX_SUFFIX}` }}
              label={label}
              inputLabels={inputLabels}
              validations={validations}
            />
          );
        });

      case 'combo':
        return filter.options.map(renderFilterOptions);
    }
  }

  /**
   * This is required since form.useSubmit() internally empties the form errors
   * before invoking any callbacks.
   *
   * See: https://github.com/ariakit/ariakit/blob/main/packages/ariakit-core/src/form/form-store.ts#L315
   */
  async function onSubmit() {
    const formState = form.getState();
    const params = new URLSearchParams(searchParams?.toString());

    function processFilter(filter: Filter) {
      params.delete(filter.name);

      switch (filter.type) {
        case 'checkbox':
          filter.options.forEach(option => {
            if (formState.values[option.id] && !formState.errors[option.id]) {
              params.append(filter.name, option.name);
            }
          });

          break;

        case 'radio':
          for (const option of filter.options) {
            if (formState.values[option.id] === option.name && !formState.errors[option.id]) {
              params.set(filter.name, option.name);
              break;
            }
          }

          break;

        case 'range':
          filter.options.forEach(option => {
            const min = formState.values[`${option.id}${RANGE_MIN_SUFFIX}`]?.toString();
            const max = formState.values[`${option.id}${RANGE_MAX_SUFFIX}`]?.toString();
            const validMin = !formState.errors[`${option.id}${RANGE_MIN_SUFFIX}`];
            const validMax = !formState.errors[`${option.id}${RANGE_MAX_SUFFIX}`];
            if (!!min && !!max && validMin && validMax) {
              params.append(filter.name, `${min} - ${max}`);
            } else if (!!min && validMin) {
              params.append(filter.name, min);
            } else if (!!max && validMax) {
              params.append(filter.name, max);
            }
          });

          break;

        case 'combo':
          filter.options.forEach(processFilter);
          break;
      }
    }

    filters.forEach(processFilter);
    router.replace(`${pathname}?${params.toString()}`, { scroll: scrollOnSubmit });
    setSidebarOpen(false);
  }

  async function onReset() {
    form.reset();
    await onSubmit();
  }

  const closeSidebar = () => {
    setSidebarOpen(false);
    applyCurrentFilters();
  };

  return (
    <DialogProvider>
      <FilterBar
        query={query}
        eyebrow={eyebrow}
        title={title}
        filters={filters}
        isSticky={isSticky}
        onButtonClick={() => {
          setSidebarOpen(true);
        }}
        ref={filterBarRef}
      />

      <Dialog
        className={styles.sidebar}
        open={sidebarOpen}
        onClose={closeSidebar}
        backdrop={<div className={styles.sidebarBackdrop} />}
      >
        <Form
          className={styles.sidebarContainer}
          store={form}
          resetOnSubmit={false}
          validateOnBlur={false}
          validateOnChange={false}
        >
          <div className={styles.sidebarHeader}>
            <DialogHeading className={typographyStyles.modulesTitle}>
              {selectedFilterCount > 0 ? `${title} (${selectedFilterCount})` : title}
            </DialogHeading>
            <DialogDismiss
              render={
                <Button
                  invert
                  size={ButtonSize.ICON}
                  className={styles.sidebarCloseButton}
                  aria-label="Close filter panel"
                  onClick={closeSidebar}
                  type="button"
                >
                  <CloseIcon className={styles.sidebarCloseButtonIcon} aria-hidden />
                </Button>
              }
            />
          </div>

          <div className={styles.filters}>
            {filters
              .filter(filter => !filter.hidden)
              .map((filter, index) => {
                const ResolvedFormGroup = filter.type === 'radio' ? FormRadioGroup : FormGroup;
                const options = renderFilterOptions(filter);
                return (
                  <ResolvedFormGroup
                    className={cx(styles.section, typographyStyles.bodySmall)}
                    key={index}
                  >
                    <FormGroupLabel className={cx(styles.title, typographyStyles.bodyLarge)}>
                      {filter.name}
                    </FormGroupLabel>

                    {filter.renderLimit ? (
                      <Expandable
                        limit={filter.renderLimit}
                        ctaText={['See more', 'See less']}
                        toggleProps={{
                          className: styles.expandableButton,
                          type: 'button',
                        }}
                      >
                        {options}
                      </Expandable>
                    ) : (
                      <>{options}</>
                    )}
                  </ResolvedFormGroup>
                );
              })}
          </div>

          <div className={styles.sidebarFooter}>
            <FormSubmit
              render={
                <AsyncButton
                  size={ButtonSize.REGULAR}
                  variant={ButtonVariant.PRIMARY}
                  onClick={onSubmit}
                >
                  View Results
                </AsyncButton>
              }
              aria-label="Apply filters"
            />
            <FormReset
              render={
                <AsyncButton
                  size={ButtonSize.REGULAR}
                  variant={ButtonVariant.SECONDARY}
                  onClick={onReset}
                >
                  Reset
                </AsyncButton>
              }
              aria-label="Clear filters"
            />
          </div>
        </Form>
      </Dialog>
    </DialogProvider>
  );
}
